import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, AlertCircle, Loader2, LogIn } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import MicrosoftSignInButton from "@/components/MicrosoftSignInButton";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(redirect);
    else setErr(res.error);
  };

  return (
    <SiteLayout branch="main">
      <div className="bg-[var(--cream)] py-16 sm:py-24 px-4">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <span className="eyebrow-dark" data-testid="login-eyebrow">MEMBER PORTAL</span>
            <h1 className="font-display text-4xl sm:text-5xl text-[var(--navy)] mt-3 leading-tight">
              Welcome back.
            </h1>
            <p className="text-[var(--muted)] mt-3 text-base leading-relaxed">
              Sign in to access purchases, memberships, and your consultation history.
            </p>
          </div>

          {/* Card */}
          <form
            onSubmit={submit}
            className="bg-white border border-[var(--line)] p-8 sm:p-10 space-y-5 shadow-sm"
            data-testid="login-form"
            noValidate
          >
            {/* OAuth options */}
            <div className="space-y-3">
              <GoogleSignInButton next={redirect} />
              <MicrosoftSignInButton next={redirect} />
            </div>

            {/* Divider */}
            <div className="relative py-2" role="separator" aria-label="Or continue with email">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[var(--line)]" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-[11px] tracking-[0.18em] text-[var(--muted)] font-semibold whitespace-nowrap">
                  OR SIGN IN WITH EMAIL
                </span>
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="login-email" className="block text-sm font-semibold text-[var(--navy)] mb-2">
                Email address
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  id="login-email"
                  required
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="w-full pl-10 pr-3 py-3 border border-[var(--line)] bg-[#fafafa] text-[15px] text-[var(--ink)] placeholder:text-neutral-400 focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="login-password" className="block text-sm font-semibold text-[var(--navy)]">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-semibold text-[var(--navy)] hover:text-[var(--gold)] underline underline-offset-2"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  id="login-password"
                  required
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="w-full pl-10 pr-11 py-3 border border-[var(--line)] bg-[#fafafa] text-[15px] text-[var(--ink)] placeholder:text-neutral-400 focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--navy)] p-1"
                  data-testid="login-toggle-password"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {err && (
              <div
                role="alert"
                className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-800 text-sm p-3.5"
                data-testid="login-error"
              >
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span className="leading-relaxed">{err}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="btn btn-navy w-full justify-center h-12 text-[15px] disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} /> Signing in…
                </>
              ) : (
                <>
                  <LogIn size={16} className="mr-2" /> Sign in
                </>
              )}
            </button>
          </form>

          {/* Footer link */}
          <p className="text-center text-sm text-[var(--muted)] mt-6">
            New to Catalyst?{" "}
            <Link
              to="/register"
              className="text-[var(--navy)] font-bold hover:text-[var(--gold)] underline underline-offset-2"
              data-testid="login-register-link"
            >
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </SiteLayout>
  );
}
